import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Spinner, Button, Table, Modal, Form } from "react-bootstrap";
import moment from "moment";
import SideMenu from "../../Components/SideMenu";
import { useDispatch, useSelector } from "react-redux";
import { adminChatGroupsList } from "../../reducers/commonReducer";
import { allApi, messages, postApi, postApiFile } from "../../helpers/apiStructure";
import logo from "../../images/logo.png";

const AdminGroupChats = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const queryParameters = new URLSearchParams(location.search);
  const agentList = queryParameters.get("agent");
  const [loading, setLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupImage, setGroupImage] = useState(null);
  const [groupImagePreview, setGroupImagePreview] = useState("");
  const [updatingImage, setUpdatingImage] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [groupUsers, setGroupUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [groupTopic, setGroupTopic] = useState("");
  const [membersLoading, setMembersLoading] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);
  const userSlug = localStorage.getItem("slug");
  const canDeleteGroup = userSlug === "admin";

  const getBranchId = (group) =>
    group?.branch?._id || group?.branch?.id || group?.branch || "";

  const getMemberIds = (group) =>
    (group?.members || []).map((member) => member?._id || member?.id || member).filter(Boolean);

  useEffect(() => {
    setLoading(true);
    dispatch(adminChatGroupsList({})).finally(() => setLoading(false));
  }, [dispatch]);

  const { adminChatUsersData = [] } = useSelector((state) => state.commonReducer);

  const refreshGroups = async () => {
    setLoading(true);
    await dispatch(adminChatGroupsList({}));
    setLoading(false);
  };

  const handleDeleteGroup = async (group) => {
    if (!canDeleteGroup) {
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${group?.name || "this group"}"?`
    );

    if (!confirmed) {
      return;
    }

    const response = await allApi(
      `${window.location.protocol}//${window.location.host}/api/chat-group/${group?.id || group?.groupId}`,
      "delete"
    );

    await messages(response?.message, response?.status);

    if (response?.status === 1) {
      await refreshGroups();
    }
  };

  const openImageModal = (group) => {
    setSelectedGroup(group);
    setGroupImage(null);
    setGroupImagePreview(group?.image || "");
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setSelectedGroup(null);
    setGroupImage(null);
    setGroupImagePreview("");
    setShowImageModal(false);
    setUpdatingImage(false);
  };

  const closeMembersModal = () => {
    setSelectedGroup(null);
    setGroupUsers([]);
    setSelectedMembers([]);
    setGroupName("");
    setGroupTopic("");
    setShowMembersModal(false);
    setMembersLoading(false);
    setSavingMembers(false);
  };

  const toggleGroupMember = (userId) => {
    setSelectedMembers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const openMembersModal = async (group) => {
    setSelectedGroup(group);
    setGroupName(group?.name || "");
    setGroupTopic(group?.topic || "");
    setSelectedMembers(getMemberIds(group));
    setGroupUsers([]);
    setShowMembersModal(true);
    setMembersLoading(true);

    try {
      const response = await postApi(
        `${window.location.protocol}//${window.location.host}/api/teamleaders`,
        "post",
        {
          page: 1,
          branch: getBranchId(group),
        }
      );

      const availableUsers = (response?.list || []).filter(
        (user) => user?.status !== false
      );

      setGroupUsers(availableUsers);
    } catch (error) {
      console.error("Group members fetch error:", error);
      setGroupUsers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleSaveMembers = async () => {
    if (!selectedGroup || !groupName.trim()) {
      return;
    }

    setSavingMembers(true);

    try {
      const response = await postApi(
        `${window.location.protocol}//${window.location.host}/api/chat-group/edit/${selectedGroup?.id || selectedGroup?.groupId}`,
        "put",
        {
          name: groupName,
          topic: groupTopic,
          members: selectedMembers,
        }
      );

      await messages(response?.message, response?.status);

      if (response?.status === 1) {
        await refreshGroups();
        closeMembersModal();
        return;
      }
    } catch (error) {
      console.error("Save group members error:", error);
    }

    setSavingMembers(false);
  };

  const handleUpdateGroupImage = async () => {
    if (!selectedGroup || !groupImage) {
      return;
    }

    setUpdatingImage(true);
    const response = await postApiFile(
      `${window.location.protocol}//${window.location.host}/api/chat-group/image/${selectedGroup?.id || selectedGroup?.groupId}`,
      "put",
      { image: groupImage }
    );

    await messages(response?.message, response?.status);

    if (response?.status === 1) {
      await refreshGroups();
      closeImageModal();
      return;
    }

    setUpdatingImage(false);
  };

  return (
    <>
      <SideMenu />
      <div className="mt-extra content container-fluid">
        <div className="p-2 mbl-pedding-10 min-vh-100">
          <div className="mb-3">
            <h3 className="mb-1">Group Chats</h3>
            <p className="text-muted mb-0">View all groups, members, branch names, and open the full group conversation.</p>
          </div>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner animation="grow" />
            </div>
          ) : adminChatUsersData.length > 0 ? (
            <div className="table-responsive bg-white rounded shadow-sm">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Group</th>
                    <th>Members</th>
                    <th>Branch</th>
                    <th>Last Chat</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {adminChatUsersData.map((item, index) => (
                    <tr key={item?.id || item?.groupId || index}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="fw-bold">{item?.name || "Group Chat"}</div>
                        {item?.topic && <div className="text-muted small">{item.topic}</div>}
                      </td>
                      <td>
                        <div>{item?.memberCount || 0} members</div>
                        <div className="text-muted small">
                          {item?.memberNames?.length ? item.memberNames.join(", ") : "No members"}
                        </div>
                      </td>
                      <td>{item?.branchName || "-"}</td>
                      <td>
                        {item?.lastMessage?.createdAt
                          ? moment(item.lastMessage.createdAt).format("DD MMM YYYY, hh:mm A")
                          : "-"}
                      </td>
                      <td>
                        <Button
                          variant="secondary"
                          className="me-2"
                          onClick={() => openImageModal(item)}
                        >
                          Update DP
                        </Button>
                        <Button
                          variant="primary"
                          className="me-2"
                          onClick={() => openMembersModal(item)}
                        >
                          Manage Members
                        </Button>
                        <Button
                          variant="info"
                          className="me-2"
                          onClick={() =>
                            navigate(
                              `/admin-group-chat?groupId=${item?.id || item?.groupId}&name=${encodeURIComponent(
                                item?.name || "Group Chat"
                              )}&time=${item?.lastMessage?.createdAt || ""}&agent=${agentList || ""}`
                            )
                          }
                        >
                          View Chat
                        </Button>
                        {canDeleteGroup && (
                          <Button
                            variant="danger"
                            onClick={() => handleDeleteGroup(item)}
                          >
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center mt-5 d-flex justify-content-center align-items-center">
              <h4 className="text-danger">No group chats found.</h4>
            </div>
          )}
        </div>
      </div>
      <Modal show={showImageModal} onHide={closeImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Group DP</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <img
              className="profile-img"
              src={groupImagePreview || selectedGroup?.image || logo}
              alt={selectedGroup?.name || "Group"}
            />
          </div>
          <Form.Group>
            <Form.Label htmlFor="admin-group-image">Group DP</Form.Label>
            <Form.Control
              id="admin-group-image"
              type="file"
              accept="image/*"
              onChange={(event) => {
                const selectedFile = event.target.files[0];
                setGroupImage(selectedFile || null);
                setGroupImagePreview(
                  selectedFile ? URL.createObjectURL(selectedFile) : selectedGroup?.image || ""
                );
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeImageModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleUpdateGroupImage}
            disabled={updatingImage || !groupImage}
          >
            {updatingImage ? "Updating..." : "Update DP"}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showMembersModal} onHide={closeMembersModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Manage Group Members</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Group Name</Form.Label>
            <Form.Control
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              placeholder="Enter group name"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Topic</Form.Label>
            <Form.Control
              value={groupTopic}
              onChange={(event) => setGroupTopic(event.target.value)}
              placeholder="Enter discussion topic"
            />
          </Form.Group>
          <Form.Label>Select Members</Form.Label>
          {membersLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="chat-forward-users">
              {groupUsers.map((user) => {
                const userId = user?.id || user?._id;
                const fullName =
                  `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                  "No Name";

                return (
                  <label key={userId} className="chat-forward-user">
                    <img
                      className="profile-img"
                      src={user?.image || logo}
                      alt={fullName}
                    />
                    <div className="chat-forward-user-copy">
                      <strong>{fullName}</strong>
                    </div>
                    <Form.Check
                      type="checkbox"
                      checked={selectedMembers.includes(userId)}
                      onChange={() => toggleGroupMember(userId)}
                    />
                  </label>
                );
              })}
              {!groupUsers.length && (
                <div className="text-muted py-2">No users available for this branch.</div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeMembersModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSaveMembers}
            disabled={savingMembers || !groupName.trim()}
          >
            {savingMembers ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminGroupChats;
